/**
 * Processa strings com variáveis no formato {{entidade.campo}}
 * e substitui pelos valores reais dos dados fornecidos
 *
 * Formato esperado das variáveis:
 * - {{account.name}} -> Nome da conta
 * - {{account.cnpj}} -> CNPJ da conta
 * - {{contact.name}} -> Nome do contato
 * - {{contact.email}} -> Email do contato
 * - {{customField_123}} -> Campo customizado pelo ID
 *
 * @param {string} text - Texto contendo variáveis para processar
 * @param {Object} data - Objeto com os dados disponíveis
 * @param {Object} data.account - Dados da conta
 * @param {Object} data.contact - Dados do contato
 * @param {Object} data.customFields - Campos customizados (chave = id do campo)
 * @param {boolean} addLabels - Se true, adiciona labels (ex: "Nome: João")
 * @param {Object} customFieldsLabels - Mapeamento de nomes de campos customizados para labels da API (ex: {"teste_customfield_account": "teste customField ACCOUNT"})
 * @returns {string} Texto processado com valores substituídos
 */
export function processProposalVariables(
  text,
  data = {},
  addLabels = false,
  customFieldsLabels = {}
) {
  if (!text || typeof text !== 'string') return text;

  const { account = {}, contact = {}, customFields = {} } = data;

  // Mapeamento de campos padrão para labels em português
  const fieldLabels = {
    name: 'Nome',
    email: 'E-mail',
    phone: 'Telefone',
    ddi: 'DDI',
    customerPortfolioId: 'Categoria dos Clientes',
    responsible: 'Responsável'
  };

  // Função auxiliar para formatar o nome do campo customizado como label
  const formatCustomFieldLabel = (fieldName) => {
    // Remove prefixos comuns: teste_, campo_, custom_, etc.
    let formatted = fieldName.replace(
      /^(teste_|campo_|custom_field_|customfield_)/gi,
      ''
    );

    // Substitui underscores por espaços e capitaliza cada palavra
    formatted = formatted
      .split('_')
      .map((word) => {
        // Se a palavra for toda maiúscula (ex: ACCOUNT), mantém como está
        if (word === word.toUpperCase() && word.length > 1) {
          return word;
        }
        // Senão, capitaliza a primeira letra
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');

    return formatted;
  };

  // Regex para encontrar padrões {{algo}}
  const variablePattern = /\{\{([^}]+)\}\}/g;

  return text.replace(variablePattern, (match, variable) => {
    const trimmedVar = variable.trim();
    let value = null;
    let label = '';

    // Verifica se é um campo de conta (account.campo)
    if (trimmedVar.startsWith('account.')) {
      const field = trimmedVar.substring('account.'.length);

      // Tratamento especial para telefone: concatena DDI + telefone
      if (field === 'phone') {
        const ddi = account.ddi ? `+${account.ddi}` : '';
        const phone = account.phone || '';
        value = ddi && phone ? `${ddi} ${phone}` : phone || ddi || null;
      }
      // Tratamento especial para customerPortfolioId: busca o nome da categoria
      else if (field === 'customerPortfolioId') {
        value = account.customerPortfolioName || account[field] || null;
      }
      // Campos normais
      else {
        value =
          account[field] !== undefined && account[field] !== null
            ? String(account[field])
            : null;
      }

      // Para campos padrão, usa o mapeamento; para custom fields, usa label da API ou formata o nome
      if (fieldLabels[field]) {
        label = fieldLabels[field];
      } else if (customFieldsLabels && customFieldsLabels[field]) {
        // Usa o label da API se disponível
        label = customFieldsLabels[field];
      } else {
        // Fallback: formata o nome do campo
        label = formatCustomFieldLabel(field);
      }
    }

    // Verifica se é um campo de contato (contact.campo)
    else if (trimmedVar.startsWith('contact.')) {
      const field = trimmedVar.substring('contact.'.length);

      // Tratamento especial para telefone: concatena DDI + telefone
      if (field === 'phone') {
        const ddi = contact.ddi ? `+${contact.ddi}` : '';
        const phone = contact.phone || '';
        value = ddi && phone ? `${ddi} ${phone}` : phone || ddi || null;
      }
      // Campos normais
      else {
        value =
          contact[field] !== undefined && contact[field] !== null
            ? String(contact[field])
            : null;
      }

      // Para campos padrão, usa o mapeamento; para custom fields, usa label da API ou formata o nome
      if (fieldLabels[field]) {
        label = fieldLabels[field];
      } else if (customFieldsLabels && customFieldsLabels[field]) {
        // Usa o label da API se disponível
        label = customFieldsLabels[field];
      } else {
        // Fallback: formata o nome do campo
        label = formatCustomFieldLabel(field);
      }
    }

    // Verifica se é um campo customizado (customField_id) - formato antigo
    else if (trimmedVar.startsWith('customField_')) {
      const fieldId = trimmedVar.substring('customField_'.length);
      value =
        customFields[fieldId] !== undefined && customFields[fieldId] !== null
          ? String(customFields[fieldId])
          : null;
      label = `Campo ${fieldId}`;
    }

    // Se addLabels é true, retorna "Label: Valor" ou "Label:" se valor vazio
    if (addLabels && label) {
      if (value === null || value === '') {
        return `${label}:`;
      }
      return `${label}: ${value}`;
    }

    // Se não tem label, retorna vazio quando não há valor
    if (value === null) return '';

    // Caso contrário, retorna apenas o valor
    return value;
  });
}

/**
 * Adiciona prefixo de entidade a um campo para identificação posterior
 * Usado ao salvar variáveis na proposta
 *
 * @param {string} fieldName - Nome do campo (ex: 'name', 'email')
 * @param {string} entity - Entidade de origem ('account', 'contact', 'customField')
 * @param {string} [customFieldId] - ID do campo customizado (se aplicável)
 * @returns {string} Variável formatada (ex: '{{account.name}}')
 */
export function formatVariableWithPrefix(
  fieldName,
  entity,
  customFieldId = null
) {
  if (entity === 'customField' && customFieldId) {
    return `{{customField_${customFieldId}}}`;
  }
  return `{{${entity}.${fieldName}}}`;
}

/**
 * Extrai todas as variáveis presentes em um texto
 *
 * @param {string} text - Texto para analisar
 * @returns {Array<string>} Lista de variáveis encontradas (ex: ['account.name', 'contact.email'])
 */
export function extractVariables(text) {
  if (!text || typeof text !== 'string') return [];

  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = [];
  let match;

  while ((match = variablePattern.exec(text)) !== null) {
    variables.push(match[1].trim());
  }

  return variables;
}
